// HER DASHBOARD ITEM, SURFACE 2(a) — **SITE TRAFFIC AT THE VERY TOP.** Her own words and her own
// order: visitors for the period, page views, NEW vs RETURNING, and where they came from.
//
// **IT IS HOOKLESS AND TAKES ITS DATA AS A PROP, SO A GATE CALLS IT** (R7/R12's entry requirement).
// The analytics tab already reads one payload for the whole tab; a second fetch here would be a
// second answer to *what period is this* on a dashboard whose whole D6-3 ruling is one range control
// — and a component with a read of its own could only ever be checked by a regex.
//
// **EVERY NUMBER IS THE SHARED LAW'S.** `trafficLine` derives `returning` in ONE place and floors it;
// `trafficSourceRows` decides what may appear and in what order. Nothing is re-derived here, because
// a surface that does its own arithmetic is a second answer key (R28/R29).
import { trafficLine, trafficSourceRows, trafficSourceLabel, visitCoverage, trafficCoverageNote, TRAFFIC_ATTRIBUTION_NOTE,
  VISIT_CLASSES, visitClassLabel, trafficClassLine, trafficClassSources, TRAFFIC_CLASS_NOTE } from "../traffic.js";

export type TrafficPayload = {
  views: number;
  visitors: number;
  newVisitors: number;
  sources: Array<{ source: string; visitors: number }>;
  // ITEM 9 / T3 — the conversion half, keyed on the visitor's FIRST source. It is its own list
  // because it is its own population, and the law is what joins them.
  conversions?: Array<{ source: string; arrivals: number; signups: number }>;
  // HER TRAFFIC SPLIT (2026-08-18). Optional, because a browser holding a bundle older than that
  // round has no split in its payload and must draw NO box rather than a row of zeros (R18/R65).
  classes?: Record<string, { views: number; visitors: number; newVisitors: number; sources: Array<{ source: string; visitors: number }> }>;
  collectingSince: string | null;
  rangeStartMs?: number;
};

const short = (n: number) => new Intl.NumberFormat("en-US").format(n);

type SourceRow = { source: string; visitors: number; arrivals: number; reachedDoor: number; signups: number; paid: number; pct: number | null; doorPct: number | null; signupPct: number | null; paidPct: number | null };

export function OwnerTraffic({ traffic, rangeLabel }: { traffic: TrafficPayload | null | undefined; rangeLabel: string }) {
  // >>> THREE STATES, NEVER TWO (R18/R65), AND THE MIDDLE ONE IS THE ONE THAT COSTS SOMETHING. <<<
  // `undefined` is a payload still in flight and HOLDS STILL. `null` is a read that ARRIVED AND
  // COULD NOT BE MADE, and it says so — rendering it as zeroes puts "nobody visited your site" on
  // the top line of her analytics on a day the database refused a query, which is a number she would
  // act on. Zero is an ANSWER and is allowed to draw as zero.
  if (traffic === undefined) return null;
  if (traffic === null) {
    return (
      <section className="owner-traffic" data-traffic-state="unread">
        <h3 className="owner-traffic__title">Site traffic</h3>
        <p className="owner-traffic__note">These numbers could not be read just now.</p>
      </section>
    );
  }

  const line = trafficLine(traffic);
  const rows: SourceRow[] = trafficSourceRows(traffic.sources, traffic.conversions || []);
  // >>> **HER REPORT, 2026-08-14: THE SAME TOTALS FOR TODAY, 7 DAYS AND 10 DAYS.** <<< Traced against
  // `site_visits` itself: **9,299 rows across ONE Pacific day**, because the collector's own migration
  // only reached production this morning — so every window really does contain the same set and the
  // tiles were arithmetically honest while reading as broken.
  //
  // **THE FOOTNOTE ALREADY SAID *counted since*, AND IT SAT UNDER A TABLE AT THE FOOT OF THE SECTION
  // WHILE THE SAMENESS IS IN THE TILES AT THE TOP.** A caveat she has to scroll past three identical
  // numbers to reach is a caveat that arrives after the conclusion. It is drawn beside the numbers it
  // is about now, and it NAMES THE SAMENESS rather than only the date.
  //
  // **AND THE QUESTION IS ASKED THROUGH `visitCoverage`, WHICH ALREADY ANSWERED IT FOR THE FUNNEL
  // CARD** (R28/R29): *does this window reach back before counting began* had one law and one reader,
  // and the surface her own report is about was not the reader. A second spelling here would be two
  // answers to whether a number is trustworthy, on the two screens that both claim to be.
  const coverage = visitCoverage({ collectingSince: traffic.collectingSince, rangeStartMs: traffic.rangeStartMs });

  return (
    <section className="owner-traffic" data-traffic-state="read" data-traffic-coverage={coverage}>
      <h3 className="owner-traffic__title">Site traffic</h3>
      {coverage === "measured" ? null : (
        <p className="owner-traffic__note owner-traffic__coverage">{trafficCoverageNote(coverage, traffic.collectingSince)}</p>
      )}
      <div className="owner-traffic__tiles">
        <div className="owner-traffic__tile" data-traffic-tile="visitors">
          <span className="owner-traffic__n">{short(line.visitors)}</span>
          <span className="owner-traffic__k">Visitors</span>
        </div>
        <div className="owner-traffic__tile" data-traffic-tile="views">
          <span className="owner-traffic__n">{short(line.views)}</span>
          <span className="owner-traffic__k">Page views</span>
        </div>
        <div className="owner-traffic__tile" data-traffic-tile="new">
          <span className="owner-traffic__n">{short(line.newVisitors)}</span>
          {/* A PERCENTAGE OF NOTHING IS NOTHING, NOT ZERO (R18). `newPct` is null on an empty period
              and the surface prints nothing rather than "0%", which reads as a fact about her
              audience rather than as an absence of one. */}
          <span className="owner-traffic__k">New{line.newPct === null ? "" : ` · ${line.newPct}%`}</span>
        </div>
        <div className="owner-traffic__tile" data-traffic-tile="returning">
          <span className="owner-traffic__n">{short(line.returning)}</span>
          <span className="owner-traffic__k">Returning{line.returningPct === null ? "" : ` · ${line.returningPct}%`}</span>
        </div>
      </div>

      {/* ==========================================================================================
          >>> HER TRAFFIC SPLIT (2026-08-18). TOTAL STAYS THE HEADLINE, ABOVE; THESE ARE THE TWO
          BOXES UNDER IT. <<<

          Her clause 2 is the ADS' REPORT CARD -- strangers counted on their own, with their own
          sources ranked -- and her clause 3 is the companion box, so community engagement can be
          watched separately from acquisition.

          **A CLASS THE PAYLOAD DOES NOT CARRY DRAWS NOTHING RATHER THAN A ROW OF ZEROS (R18/R65):**
          a browser holding a bundle older than this round is *we have not been told*, and three
          tiles of `0` under Non-member traffic says her advertising brought nobody -- a claim about
          her spend rather than about a payload.
          ========================================================================================== */}
      {VISIT_CLASSES.map((cls) => {
        const cl = trafficClassLine(traffic, cls);
        if (!cl) return null;
        const src = trafficClassSources(traffic, cls);
        return (
          <div className="owner-traffic__class" key={cls} data-traffic-class={cls}>
            <h4 className="owner-traffic__sub">{visitClassLabel(cls)}</h4>
            <div className="owner-traffic__tiles owner-traffic__tiles--class">
              <div className="owner-traffic__tile" data-traffic-class-tile={`${cls}:visitors`}>
                <span className="owner-traffic__n">{short(cl.visitors)}</span>
                <span className="owner-traffic__k">Visitors</span>
              </div>
              <div className="owner-traffic__tile" data-traffic-class-tile={`${cls}:views`}>
                <span className="owner-traffic__n">{short(cl.views)}</span>
                <span className="owner-traffic__k">Page views</span>
              </div>
              <div className="owner-traffic__tile" data-traffic-class-tile={`${cls}:new`}>
                <span className="owner-traffic__n">{short(cl.newVisitors)}</span>
                <span className="owner-traffic__k">New{cl.newPct === null ? "" : ` · ${cl.newPct}%`}</span>
              </div>
              <div className="owner-traffic__tile" data-traffic-class-tile={`${cls}:returning`}>
                <span className="owner-traffic__n">{short(cl.returning)}</span>
                <span className="owner-traffic__k">Returning{cl.returningPct === null ? "" : ` · ${cl.returningPct}%`}</span>
              </div>
            </div>
            {/* HER CLAUSE 2's SECOND HALF: the sources ranked FOR THIS CLASS. On the stranger box
                that is the ads' report card; on the member box it is where her own people arrive
                from. Drawn only where there is something to rank, or it is a heading over nothing
                (T9-2). */}
            {src.length === 0 ? null : (
              <table className="owner-table owner-traffic__sources">
                <thead><tr><th>Source</th><th>Visitors</th><th>Share</th></tr></thead>
                <tbody>
                  {src.map((r) => (
                    <tr key={r.source} data-traffic-class-source={`${cls}:${r.source}`}>
                      <td>{trafficSourceLabel(r.source)}</td>
                      <td>{short(r.visitors)}</td>
                      <td>{r.pct === null ? "" : `${r.pct}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
      {/* >>> HER CLAUSE 4, THE HONEST EDGE, NAMED ON THE SURFACE. <<< One line, the shared law's, and
          it says BOTH halves: a member browsing signed out is counted as a stranger because the web
          cannot know better, and the visitor counts can therefore add to a little MORE than the
          total while the page views add up exactly. A screen that claimed two numbers summed when
          they do not is the kind of thing she checks once and never trusts again (R141). */}
      {trafficClassLine(traffic, "stranger") ? (
        <p className="owner-traffic__note owner-traffic__split" data-traffic-split-note="1">{TRAFFIC_CLASS_NOTE}</p>
      ) : null}

      <h4 className="owner-traffic__sub">Where they came from</h4>
      {rows.length === 0 ? (
        // R43's M8 on her own screen: an empty table and a broken one look identical, so the empty
        // case says which it is IN HER WORDS rather than drawing a heading over nothing (T9-2).
        <p className="owner-traffic__note">No visits recorded in {rangeLabel}.</p>
      ) : (
        <>
          <table className="owner-table owner-traffic__sources">
            {/* ITEM 9 / T3 — **NEW HERE and JOINED are two cells about the same cohort, and VISITORS
                is about a different one.** The heading says *New here* rather than *Visitors* on that
                column precisely so the two denominators cannot be read as one: the rate underneath
                is joined ÷ new here, and it is the law's, never arithmetic typed into this markup. */}
            <thead><tr><th>Source</th><th>Visitors</th><th>Share</th><th>New here</th><th>Reached a door</th><th>Joined</th><th>Paying</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.source} data-traffic-source={r.source}>
                  <td>{trafficSourceLabel(r.source)}</td>
                  <td>{short(r.visitors)}</td>
                  <td>{r.pct === null ? "" : `${r.pct}%`}</td>
                  <td>{short(r.arrivals)}</td>
                  {/* A rate with no cohort under it is NOTHING, not 0% (R18) — nobody new arrived
                      from this source in this window, and "0%" there reads as a source whose people
                      never join, which is a claim about her advertising rather than about a period. */}
                  {/* >>> A5 — THE RUNG THAT SEPARATES *THIS CHANNEL IS BAD* FROM *THIS CHANNEL'S
                      LINKS ARE POINTING AT THE WRONG PAGE.* <<< Measured on her own rows for
                      2026-08-22: TikTok brought 68% of the day and TWO THIRDS of it landed on the
                      home page rather than on a funnel door. Arrivals and signups alone cannot show
                      that — both channels read as *arrivals*, and the difference is invisible. */}
                  <td data-traffic-door={r.source}>
                    {short(r.reachedDoor)}{r.doorPct === null ? "" : ` · ${r.doorPct}%`}
                  </td>
                  <td data-traffic-signups={r.source}>
                    {short(r.signups)}{r.signupPct === null ? "" : ` · ${r.signupPct}%`}
                  </td>
                  {/* Of the people who JOINED from this source, how many are paying — the signups are
                      the denominator on purpose, because *how well does this channel sell* and *how
                      well does it bring people* are two questions (R18). */}
                  <td data-traffic-paid={r.source}>
                    {short(r.paid)}{r.paidPct === null ? "" : ` · ${r.paidPct}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* HER *HONEST LABELLING WHERE ATTRIBUTION IS UNKNOWABLE* CLAUSE — one plain sentence, and
              it is the shared law's so the table and any other reader of it say the same thing. */}
          <p className="owner-traffic__note owner-traffic__attribution">{TRAFFIC_ATTRIBUTION_NOTE}</p>
        </>
      )}

      {/* >>> HER ACCURACY LAW: EVERY NUMBER TRACEABLE TO ITS SOURCE, NO ESTIMATES DRESSED AS COUNTS.
          <<< The collector records forward only — the page-view table it sits beside carries neither a
          visitor nor a source, so a back-fill could only invent both (R141). A period that reaches
          back before it started is honestly short, and this line is what stops that being read as a
          quiet month. F5's `visitsMeasured` shape, one surface over. */}
      {/* **THE FOOT LINE IS KEPT AND IS NOT A SECOND COPY OF THE ONE ABOVE (R28/R29).** It answers
          *when did counting begin*, which is true of every period; the line beside the tiles answers
          *is THIS period bigger than the data*, which is only sometimes true and is what she was
          reading as breakage. Two facts (R18), and on a fully-covered period only this one draws. */}
      {traffic.collectingSince ? (
        <p className="owner-traffic__note owner-traffic__since">
          Traffic has been counted since {String(traffic.collectingSince).slice(0, 10)}. Anything before that is not in these numbers.
        </p>
      ) : (
        <p className="owner-traffic__note owner-traffic__since">
          Traffic counting has not recorded its first visitor yet.
        </p>
      )}
    </section>
  );
}
